import { Box, ChakraProvider, Link, Text } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

import { Button } from '@m/components-ui/button';
import { getTheme } from '@m/components-ui/themes/registry';
import { SUPPORT_URL } from '@m/constants/Contacts';
import initApp from '@m/utils/initApp';
import { captureReservationAttributionFromUrl } from '@m/utils/reservationFunnelContext';
import CustomerApp from 'apps/customer/CustomerApp';

// Sign-in and other entry routes replace the URL without its query string. Store UTMs,
// gclid, and start_domain/start_page first so later funnel events keep the marketing
// attribution instead of recording a fresh `direct` visit.
captureReservationAttributionFromUrl();

initApp('customer');

const root = createRoot(document.getElementById('root')!);

/**
 * Shown when the app crashes while rendering (reported to Sentry), instead of a blank page. It
 * renders outside the app's providers, so it brings only the Chakra theme: just enough to match
 * the flow's other error screens.
 */
function CrashFallback() {
  return (
    <ChakraProvider value={getTheme('default')}>
      <Box px="4" pt="10" display="flex" flexDirection="column" gap="4">
        <Text variant="Subheader">Something went wrong.</Text>
        <Text variant="MainBodyRegular">Reload the page to pick up where you left off.</Text>
        <Button variant="secondary" size="lg" width="full" onClick={() => window.location.reload()}>
          Reload
        </Button>
        <Link href={SUPPORT_URL} alignSelf="center">
          Contact support
        </Link>
      </Box>
    </ChakraProvider>
  );
}

if (!navigator.cookieEnabled) {
  document.getElementById('initial-loader')?.remove();
  root.render(
    <div style={{ fontFamily: 'sans-serif', fontSize: 20, fontWeight: 'bold' }}>
      Please turn on cookies to use Metropolis.
    </div>,
  );
} else {
  root.render(
    <Sentry.ErrorBoundary fallback={<CrashFallback />}>
      <CustomerApp />
    </Sentry.ErrorBoundary>,
  );
}
